// ============================================================
// components/stashcard/HistoryEntry.tsx
//
// One row in the History timeline. Channel-coded chip + title +
// optional duration badge + body + relative time.
// ============================================================

import { Globe, Mail, MapPin, MessageSquare, Phone, Share2, StickyNote } from 'lucide-react';
import { Badge as _Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { HistoryItem, HistoryKind } from '../../store/stash-card-detail/types';
import { relativeTime } from '../../store/stash-card-detail/pipeline';

// shadcn Badge in this repo requires a render prop; cast for ergonomics.
const Badge = _Badge as React.ComponentType<{
  variant?: string;
  className?: string;
  children?: React.ReactNode;
}>;

interface HistoryEntryProps {
  item: HistoryItem;
  /** Optional: pass a fixed "now" for deterministic tests. */
  now?: Date;
}

const KIND_TO_ICON: Record<HistoryKind, React.ComponentType<{ className?: string; size?: number; strokeWidth?: number }>> = {
  call: Phone,
  text: MessageSquare,
  email: Mail,
  web: Globe,
  map: MapPin,
  note: StickyNote,
  share: Share2,
};

/**
 * Channel-tint classes. Each maps to a soft background + a darker
 * foreground for the icon. Uses semantic tokens where possible; raw
 * fallback values come from the design system's category-tint palette.
 */
const KIND_TO_TINT: Record<HistoryKind, string> = {
  call: 'bg-[var(--stash-blue)]/10 text-[var(--stash-blue-dark)]',
  text: 'bg-[var(--stash-green)]/15 text-[var(--stash-green)]',
  email: 'bg-accent text-accent-foreground',
  web: 'bg-[var(--stash-teal)]/15 text-[var(--stash-teal)]',
  map: 'bg-[var(--stash-pink)]/15 text-[var(--stash-pink-mid)]',
  note: 'bg-[var(--stash-gold)]/20 text-[var(--stash-gold)]',
  share: 'bg-muted text-muted-foreground',
};

export function HistoryEntry({ item, now }: HistoryEntryProps): React.JSX.Element {
  const Icon = KIND_TO_ICON[item.kind];
  const occurred = new Date(item.occurredAt);
  const when = relativeTime(occurred, now);

  return (
    <article
      data-kind={item.kind}
      className="grid grid-cols-[2.5rem_1fr_auto] items-start gap-3 rounded-xl border border-border bg-card p-3.5 transition-colors hover:border-border/80"
    >
      <span
        className={cn(
          'flex h-9 w-9 items-center justify-center rounded-lg',
          KIND_TO_TINT[item.kind],
        )}
        aria-hidden="true"
      >
        <Icon size={17} strokeWidth={2} />
      </span>
      <div className="min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <h4 className="text-sm font-semibold text-foreground leading-tight">{item.title}</h4>
          {item.duration && (
            <Badge variant="secondary" className="h-5 rounded-full px-2 text-[10px] font-medium">
              {item.duration}
            </Badge>
          )}
        </div>
        {item.body && (
          <p
            className={cn(
              'mt-1 text-sm leading-snug text-muted-foreground',
              item.kind === 'text' && 'italic',
            )}
          >
            {item.kind === 'text' ? `“${item.body}”` : item.body}
          </p>
        )}
        <p className="mt-1 text-[11px] font-medium text-muted-foreground/80">
          {item.author === 'You' ? 'You' : `by ${item.author}`}
        </p>
      </div>
      <time
        dateTime={item.occurredAt}
        className="shrink-0 pt-0.5 text-xs font-medium text-muted-foreground"
      >
        {when}
      </time>
    </article>
  );
}
